// arcs.js — draws reference arcs over sentences laid out by the browser.
//
// arcs.lua marks words as .ph-arc-word with data-arc-id; sources also carry
// data-arc-to (an id, or "?" for an arc into the void). Labels arrive as
// hidden .ph-arc-label[data-arc-for] elements. Positions depend on the actual
// line layout, so the arcs are measured and drawn here, and again whenever
// the block changes size (resize, fonts loading, a margin opening).
//
// Geometry mirrors the LaTeX version in arcs.lua:
//   same line       → a curve above the line, source top to target top
//   target below    → a connector from the source's bottom to the target's top
//   target above    → the reverse
//   void ("?")      → a curve up and to the right, ending in a circled "?"

const SVG_NS = 'http://www.w3.org/2000/svg';
const READY = 'data-ph-arcs-ready';
let markerSeq = 0;

function el(name, attrs) {
    const node = document.createElementNS(SVG_NS, name);
    for (const [k, v] of Object.entries(attrs)) node.setAttribute(k, String(v));
    return node;
}

function box(node, origin) {
    const r = node.getBoundingClientRect();
    return {
        left: r.left - origin.left,
        right: r.right - origin.left,
        top: r.top - origin.top,
        bottom: r.bottom - origin.top,
        cx: (r.left + r.right) / 2 - origin.left,
        height: r.height,
    };
}

// Point on a cubic Bézier at t — label placement.
function bezier(p0, p1, p2, p3, t) {
    const u = 1 - t;
    return {
        x: u * u * u * p0.x + 3 * u * u * t * p1.x + 3 * u * t * t * p2.x + t * t * t * p3.x,
        y: u * u * u * p0.y + 3 * u * u * t * p1.y + 3 * u * t * t * p2.y + t * t * t * p3.y,
    };
}

export function arcGeometry(src, tgt, lift) {
    if (!tgt) {
        const p0 = { x: src.cx, y: src.top };
        const p3 = { x: src.right + 2.4 * lift, y: src.top - 0.4 * lift };
        const p1 = { x: p0.x, y: p0.y - lift };
        const p2 = { x: p3.x - 0.4 * lift, y: p3.y - lift };
        return { p0, p1, p2, p3, void: true };
    }
    const sameLine = Math.abs(src.top - tgt.top) < Math.min(src.height, tgt.height) / 2;
    if (sameLine) {
        const p0 = { x: src.cx, y: src.top };
        const p3 = { x: tgt.cx, y: tgt.top };
        return { p0, p1: { x: p0.x, y: p0.y - lift }, p2: { x: p3.x, y: p3.y - lift }, p3, sameLine: true };
    }
    if (tgt.top > src.top) {
        const p0 = { x: src.cx, y: src.bottom };
        const p3 = { x: tgt.cx, y: tgt.top };
        return { p0, p1: { x: p0.x, y: p0.y + lift }, p2: { x: p3.x, y: p3.y - lift }, p3 };
    }
    const p0 = { x: src.cx, y: src.top };
    const p3 = { x: tgt.cx, y: tgt.bottom };
    return { p0, p1: { x: p0.x, y: p0.y - lift }, p2: { x: p3.x, y: p3.y + lift }, p3 };
}

// Where a label goes, as in arcs.lua: over an arc, on top of its apex; into
// the void, after the circled "?"; on a connector between two sentences,
// beside the line 30 % down (before any crossing), on the side it travels away from — so the labels
// of two crossing connectors sit outside the crossing.
export function labelPlacement(g, lineHeight) {
    if (g.void) return { x: g.p3.x + 16, y: g.p3.y, align: 'left' };
    if (g.sameLine) {
        const at = bezier(g.p0, g.p1, g.p2, g.p3, 0.5);
        return { x: at.x, y: at.y - 0.55 * lineHeight, align: 'center' };
    }
    const at = bezier(g.p0, g.p1, g.p2, g.p3, 0.3);
    return g.p3.x > g.p0.x
        ? { x: at.x - 4, y: at.y, align: 'right' }
        : { x: at.x + 4, y: at.y, align: 'left' };
}

const SHIFT = { left: '0', center: '-50%', right: '-100%' };

function placeLabel(label, g, lineHeight) {
    const p = labelPlacement(g, lineHeight);
    label.style.left = `${p.x}px`;
    label.style.top = `${p.y}px`;
    label.style.transform = `translate(${SHIFT[p.align]}, -50%)`;
}

function draw(root) {
    root.querySelectorAll(':scope > svg.ph-arc-svg').forEach((n) => n.remove());
    const origin = root.getBoundingClientRect();
    const words = new Map();
    root.querySelectorAll('.ph-arc-word[data-arc-id]').forEach((w) => words.set(w.dataset.arcId, w));
    const lineHeight = parseFloat(getComputedStyle(root).fontSize) || 16;
    const lift = lineHeight * 1.25;

    const svg = el('svg', { class: 'ph-arc-svg', 'aria-hidden': 'true' });
    const markerId = `ph-arc-head-${++markerSeq}`;
    const defs = el('defs', {});
    const marker = el('marker', {
        id: markerId, viewBox: '0 0 10 10', refX: 9, refY: 5,
        markerWidth: 7, markerHeight: 7, orient: 'auto-start-reverse',
    });
    marker.appendChild(el('path', { d: 'M0,0 L10,5 L0,10 z', fill: 'currentColor' }));
    defs.appendChild(marker);
    svg.appendChild(defs);

    root.querySelectorAll('.ph-arc-word[data-arc-to]').forEach((srcEl) => {
        const src = box(srcEl, origin);
        const tgtEl = srcEl.dataset.arcTo === '?' ? null : words.get(srcEl.dataset.arcTo);
        const g = arcGeometry(src, tgtEl ? box(tgtEl, origin) : null, lift);
        const d = `M${g.p0.x},${g.p0.y} C${g.p1.x},${g.p1.y} ${g.p2.x},${g.p2.y} ${g.p3.x},${g.p3.y}`;
        const path = el('path', {
            d, fill: 'none', stroke: 'currentColor', 'stroke-width': 1.5,
            'marker-end': g.void ? '' : `url(#${markerId})`,
        });
        if (srcEl.dataset.arcDashed) path.setAttribute('stroke-dasharray', '5 3');
        svg.appendChild(path);
        if (g.void) {
            svg.appendChild(el('circle', {
                cx: g.p3.x + 6, cy: g.p3.y, r: 6.5, fill: 'none', stroke: 'currentColor', 'stroke-width': 1.3,
            }));
            const q = el('text', {
                x: g.p3.x + 6, y: g.p3.y + 3.8, 'text-anchor': 'middle', 'font-size': 10.5,
                'font-weight': 700, fill: 'currentColor',
            });
            q.textContent = '?';
            svg.appendChild(q);
        }
        const label = root.querySelector(`.ph-arc-label[data-arc-for="${CSS.escape(srcEl.dataset.arcId)}"]`);
        if (label) placeLabel(label, g, lineHeight);
    });
    root.insertBefore(svg, root.firstChild);
    root.setAttribute(READY, '');
}

export function initArcs(scope = document) {
    const roots = Array.from(scope.querySelectorAll('[data-ph-arcs]'));
    if (roots.length === 0) return;
    roots.forEach((root) => {
        draw(root);
        if (typeof ResizeObserver === 'function') {
            let pending = false;
            new ResizeObserver(() => {
                if (pending) return;
                pending = true;
                requestAnimationFrame(() => { pending = false; draw(root); });
            }).observe(root);
        }
    });
    if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(() => roots.forEach(draw));
    }
}
